"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { BrowserFrame } from "@/components/scene/BrowserFrame";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screens 1.5 / 1.6 — Real-Time catalogue + popups.
 *
 *   1.5: `showProposalPopup={false}` — Al Madar reads the live catalogue
 *        unobstructed. More critical / sensitive info appears here than
 *        on the public 0.5 profile: production schedule, capacity
 *        utilisation, volume-discount ladder, live anchor seq.
 *
 *        When `showOrbIdCreatedPopup` is also true (default on 1.5),
 *        a confirmation popup appears on mount telling Al Madar a
 *        temporary OrbitraID has been issued — real LEI will mint at
 *        first contract. OK button or auto-dismisses after ~5 s.
 *
 *   1.6: `showProposalPopup={true}` — same catalogue with the centred
 *        proposal-review popup overlaid. Decorative Submit CTA;
 *        advancing with N carries the trade through OrbID fee (1.7)
 *        and materialisation + handshake #2 (1.8).
 */
export function RealTimeCatalogue({
  showProposalPopup = true,
  showOrbIdCreatedPopup = false,
  className,
}: {
  showProposalPopup?: boolean;
  showOrbIdCreatedPopup?: boolean;
  className?: string;
}) {
  const t = useTranslations("t1Registration");
  const [orbIdPopupOpen, setOrbIdPopupOpen] = useState(false);
  const [proposalPopupOpen, setProposalPopupOpen] = useState(false);

  // Let the catalogue land first; slide the OrbitraID confirmation in after
  // ~1.5 s so the audience reads the page before the modal takes focus.
  useEffect(() => {
    if (!showOrbIdCreatedPopup) return;
    const t = setTimeout(() => setOrbIdPopupOpen(true), 1500);
    return () => clearTimeout(t);
  }, [showOrbIdCreatedPopup]);

  // Same easing for the proposal-review popup on 1.6.
  useEffect(() => {
    if (!showProposalPopup) return;
    const t = setTimeout(() => setProposalPopupOpen(true), 1500);
    return () => clearTimeout(t);
  }, [showProposalPopup]);

  // Auto-dismiss the temp-OrbitraID confirmation after ~5 s if the user
  // hasn't clicked OK first.
  useEffect(() => {
    if (!orbIdPopupOpen) return;
    const t = setTimeout(() => setOrbIdPopupOpen(false), 5000);
    return () => clearTimeout(t);
  }, [orbIdPopupOpen]);

  return (
    <div className={clsx("relative h-full", className)}>
      <BrowserFrame url={t("catalogueUrl")}>
        <div className="flex flex-col gap-5 p-6">
          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
                {t("catalogueKicker")}
              </p>
              <h2 className="mt-1 font-display text-xl font-semibold text-orbitra-paper">
                {t("catalogueTitle")}
              </h2>
              <p className="mt-0.5 text-[11px] text-orbitra-teal-soft">
                {t("catalogueSubtitle")}
              </p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full border border-state-verified/40 bg-state-verified/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-verified shadow-[0_0_18px_var(--state-verified-glow)]">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-state-verified" />
              {t("catalogueLiveStreamingTag")}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-3 text-[11px]">
            <Tile label={t("catalogueAvailableNow")}     value={t("catalogueAvailableValue")}    highlight />
            <Tile label={t("catalogueEarliestDispatch")} value={t("catalogueDispatchValue")} />
            <Tile label={t("catalogueColdChainSpec")}    value={t("catalogueColdChainValue")} />
            <Tile label={t("catalogueSpotPriceUsd")}     value={t("catalogueSpotUsdValue")} />
            <Tile label={t("catalogueSpotPriceAed")}     value={t("catalogueSpotAedValue")} />
            <Tile label={t("catalogueMoq")}              value={t("catalogueMoqValue")} />
            <Tile label={t("catalogueHalal")}            value={t("catalogueHalalValue")} />
            <Tile label={t("catalogueVet")}              value={t("catalogueVetValue")} />
            <Tile label={t("catalogueAbccOrigin")}       value={t("catalogueAbccValue")} />
          </div>

          {/* Sensitive / private-side info — not surfaced on the public 0.5 page. */}
          <div className="rounded-md border border-state-verified/30 bg-state-verified/5 p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("catalogueVerifiedBuyerBanner")}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
              <Tile label={t("catalogueProductionSchedule")} value={t("catalogueProdScheduleValue")} />
              <Tile label={t("catalogueQ3Utilisation")}      value={t("catalogueQ3UtilisationValue")} />
              <Tile label={t("catalogueVolumeLadder")}       value={t("catalogueVolumeLadderValue")} />
            </div>
          </div>

          <div className="rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-3 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("catalogueLiveAnchorTitle")}
            </p>
            <p className="mt-1 text-orbitra-paper">
              {t("catalogueLiveAnchorBody")} <span className="font-mono text-state-verified">{t("catalogueSeq")}</span>.
            </p>
          </div>

          {/* Create-proposal CTA — rendered on BOTH 1.5 and 1.6 so the
           *  browser content under the popup on 1.6 matches 1.5's layout
           *  exactly. On 1.6 the proposal-review popup sits over the top;
           *  the CTA stays visible behind it for continuity. */}
          <div className="flex items-center justify-between rounded-md border border-state-verified/40 bg-state-verified/10 px-5 py-4">
            <p className="text-[13px] text-orbitra-paper">
              {t("catalogueCtaPrompt")}
            </p>
            <button type="button" onClick={advanceScreen} className="cta-primary px-5 py-2 font-display text-sm font-semibold">
              {t("catalogueCtaButton")}
            </button>
          </div>
        </div>
      </BrowserFrame>

      {/* Temporary-OrbitraID confirmation popup — only on 1.5, auto-dismisses after ~5s.
       *  Carries the OrbitraID-emphasis treatment (thicker border, intensified
       *  glow, ORBITRA · ID corner chip) shared with the 1.8 Verified Trader badge. */}
      {orbIdPopupOpen && (
        <div
          className="absolute inset-0 z-40 grid place-items-center bg-orbitra-ink/70 backdrop-blur-sm"
          onClick={() => setOrbIdPopupOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="orbid-created-title"
            className="relative w-[min(560px,92vw)] rounded-md border-2 border-state-verified bg-orbitra-ink/95 p-5 shadow-[0_0_60px_var(--state-verified-glow),0_0_18px_var(--state-verified)] backdrop-blur-md"
            onClick={(e) => e.stopPropagation()}
          >
            <span
              aria-hidden="true"
              className="absolute -top-3 start-4 inline-flex items-center gap-1 rounded-full border border-state-verified bg-orbitra-ink px-3 py-0.5 font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-state-verified shadow-[0_0_18px_var(--state-verified-glow)]"
            >
              {t("catalogueOrbidBrandTag")}
            </span>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("catalogueOrbidCreated")}
            </p>
            <h3 id="orbid-created-title" className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
              {t("catalogueOrbidTitle")}
            </h3>
            <p className="mt-2 text-[12px] text-orbitra-teal-soft">
              {t("catalogueOrbidBody")}
            </p>

            <div className="mt-4 rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-3 text-[11px]">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldTempId")}</span>
                <span className="font-mono text-orbitra-paper" dir="ltr">TMP-ALMADAR-2026-0742</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldHolder")}</span>
                <span className="text-orbitra-paper">{t("callHolderValue")}</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldJurisdiction")}</span>
                <span className="text-orbitra-paper">{t("callJurisdictionValue")}</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldCreated")}</span>
                <span className="font-mono text-orbitra-paper" dir="ltr">{t("catalogueOrbidCreatedValue")}</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("callFieldRealLei")}</span>
                <span className="text-orbitra-muted italic">{t("callRealLeiValue")}</span>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-[10px] text-orbitra-muted">
                {t("catalogueOrbidReviewHint")}
              </p>
              <button
                type="button"
                onClick={() => setOrbIdPopupOpen(false)}
                className="cta-primary px-5 py-2 font-display text-sm font-semibold"
              >
                {t("catalogueOrbidOk")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Centred proposal-review popup — only on 1.6, not on 1.5.
       *  Gated on `proposalPopupOpen` so the catalogue is visible for ~1.5 s
       *  before the modal slides in. */}
      {proposalPopupOpen && (
      <div className="absolute inset-0 z-30 grid place-items-center bg-orbitra-ink/55 backdrop-blur-sm">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="proposal-review-title"
          className="glass w-[min(620px,92vw)] p-5"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
                {t("catalogueProposalKicker")}
              </p>
              <h3 id="proposal-review-title" className="mt-1 font-display text-base font-semibold text-orbitra-paper">
                {t("catalogueProposalTitle")}
              </h3>
            </div>
            <span className="rounded-full border border-state-verified/40 bg-state-verified/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-verified">
              {t("catalogueProposalDraftTag")}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
            <Param label={t("orbidFieldCommodity")}                  value={t("orbidCommodityValue")} />
            <Param label={t("orbidFieldQuantity")}                   value={t("orbidQuantityValue")} />
            <Param label={t("catalogueProposalTargetPrice")}         value={t("catalogueProposalTargetPriceValue")} />
            <Param label={t("catalogueProposalFunding")}             value={t("catalogueProposalFundingValue")} />
            <Param label={t("catalogueProposalSettlement")}          value={t("catalogueProposalSettlementValue")} />
            <Param label={t("orbidFieldIncoterms")}                  value={t("orbidIncotermsValue")} />
            <Param label={t("catalogueProposalDestination")}         value={t("catalogueProposalDestinationValue")} />
            <Param label={t("orbidFieldDeliveryWindow")}             value={t("orbidDeliveryWindowValue")} />
            <Param label={t("catalogueProposalColdChain")}           value={t("catalogueProposalColdChainValue")} />
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-[10px] text-orbitra-muted">
              {t("catalogueProposalNote")}
            </p>
            <button type="button" onClick={advanceScreen} className="cta-primary px-5 py-2 font-display text-sm font-semibold">
              {t("catalogueProposalSubmit")}
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function Tile({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={clsx(
      "rounded-md border bg-orbitra-ink/40 px-2.5 py-1.5",
      highlight ? "border-state-verified/50" : "border-state-verified/20",
    )}>
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className={clsx("mt-0.5 truncate", highlight ? "font-display text-sm font-semibold text-state-verified" : "text-orbitra-paper")}>{value}</p>
    </div>
  );
}

function Param({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/50 px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 truncate text-orbitra-paper">{value}</p>
    </div>
  );
}
